import { useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'

import type { LoadStatus, UnreadCountResponse } from '@fv/models'

import { type MessageInboxQueryData, messageQueryKeys } from './queries'
import type { Message, MessageInboxItem } from './types'

export const useUpdateMessagesInInboxCache = () => {
  const queryClient = useQueryClient()
  return (
    messagesModifier: (messages: MessageInboxItem[]) => MessageInboxItem[],
  ) => {
    queryClient.setQueriesData<MessageInboxQueryData>(
      {
        queryKey: messageQueryKeys.inboxRoot(),
      },
      data => {
        if (!data) return data
        return {
          ...data,
          ...(data.pages && {
            pages: data.pages.map(p => ({
              ...p,
              data: messagesModifier(p.data ?? []),
            })),
          }),
        }
      },
    )
  }
}

export const useUpdateMessagesInLoadCache = () => {
  const queryClient = useQueryClient()
  return (
    loadId: string,
    messagesModifier: (messages: Message[]) => Message[],
  ) => {
    if (!loadId) return

    queryClient.setQueriesData<Message[]>(
      {
        queryKey: messageQueryKeys.load(loadId),
      },
      data => {
        if (!data) return data
        return messagesModifier(data)
      },
    )
  }
}

const validLoadStatuses: Array<LoadStatus> = ['quoting', 'booked']
export const useUnreadCountCacheModifier = (
  message?: Pick<Message, '_id' | 'loadId' | 'loadStatus'>,
) => {
  const invalidateUnreadCount = useUnreadCountCacheInvalidator()
  const queryClient = useQueryClient()
  return (read: boolean) => {
    queryClient.setQueryData<UnreadCountResponse>(
      messageQueryKeys.unreadCount(),
      data => {
        if (!data || !validLoadStatuses.includes(message.loadStatus)) {
          return data
        }
        const key: keyof UnreadCountResponse =
          message.loadStatus === 'booked' ? 'booked' : 'quoting'
        const current = data[key]
        if (current === 0 && read) return data
        return {
          ...data,
          [key]: data[key] + (read ? -1 : 1),
        }
      },
    )
    invalidateUnreadCount()
  }
}

export const useUnreadCountCacheInvalidator = (delay = 5000) => {
  const timeoutRef = useRef<NodeJS.Timeout>(null)
  const queryClient = useQueryClient()
  return () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: messageQueryKeys.unreadCount(),
      })
    }, delay)
  }
}
